import { useCallback, useRef, useState } from 'react';

import http from '@/lib/http';
import { cmsSpecialPagesPath } from '@/lib/routes';
import type { SpecialPageMeta } from '@/types/shared';

export async function fetchSpecialPages(): Promise<SpecialPageMeta> {
  const { data } = await http.get<SpecialPageMeta>(cmsSpecialPagesPath());
  return data;
}

// Fetches special-page metadata before a dialog opens so it renders once on the
// correct step. A request id guards against a fast close+reopen applying a stale
// response.
export function useSpecialPagesBeforeOpen() {
  const [specialPages, setSpecialPages] = useState<SpecialPageMeta | undefined>();
  const [isPreparing, setIsPreparing] = useState(false);
  const requestIdRef = useRef(0);

  const prepareOpen = useCallback(async (onReady: (data: SpecialPageMeta | undefined) => void) => {
    const requestId = ++requestIdRef.current;
    setIsPreparing(true);
    try {
      const data = await fetchSpecialPages();
      if (requestId !== requestIdRef.current) return;
      setSpecialPages(data);
      onReady(data);
    } catch {
      if (requestId !== requestIdRef.current) return;
      onReady(undefined);
    } finally {
      if (requestId === requestIdRef.current) setIsPreparing(false);
    }
  }, []);

  const close = useCallback(() => {
    requestIdRef.current += 1;
    setIsPreparing(false);
  }, []);

  return { specialPages, isPreparing, prepareOpen, close };
}
